/**
 * Tiny GA4 (G-62J4T1P517) event helper for key_events / conversions.
 *
 * PDPA (Luat PDP 2026): an event fires ONLY when the user granted site_analytics
 * consent (the `asklaw_consent` choice from components/consent-banner.tsx). No
 * PII is ever sent (e.g. ask_submitted carries NO question text - legal queries
 * are sensitive). No D1 write. Safe no-op during SSG / when gtag is absent.
 *
 * Mark these as Key events in GA4 Admin -> Events (or Key events):
 *   - ask_submitted : user sent a question in /chat (core conversion)
 *   - doc_opened    : user opened a /van-ban legal document
 *   - blog_read     : user dwelt >= 25s on a blog article (engaged read)
 *
 * NOTE (separate follow-up): index.html gtag uses send_page_view:true WITHOUT
 * consent gating (no Google Consent Mode), so page_view is currently sent before
 * analytics consent. That is a pre-existing PDPA gap to close via Consent Mode v2;
 * it is intentionally NOT changed here to keep this scoped to key_events.
 */

const CONSENT_KEY = 'asklaw_consent';

/** True only when the stored consent choice has analytics === true. */
function analyticsGranted(): boolean {
  if (typeof window === 'undefined') return false;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(CONSENT_KEY);
  } catch {
    // private mode - fall through to cookie
  }
  if (!raw && typeof document !== 'undefined') {
    const cookie = document.cookie
      .split(';')
      .map((p) => p.trim())
      .find((p) => p.startsWith(`${CONSENT_KEY}=`))
      ?.slice(CONSENT_KEY.length + 1);
    if (cookie) {
      try {
        raw = decodeURIComponent(cookie);
      } catch {
        raw = cookie;
      }
    }
  }
  if (!raw) return false;
  try {
    return (JSON.parse(raw) as { analytics?: boolean }).analytics === true;
  } catch {
    return false;
  }
}

type GtagParams = Record<string, string | number | boolean>;

/** Fire a GA4 event if (and only if) analytics consent was granted. */
export function trackEvent(name: string, params?: GtagParams): void {
  if (typeof window === 'undefined') return;
  const g = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof g !== 'function') return;
  if (!analyticsGranted()) return;
  try {
    g('event', name, params ?? {});
  } catch {
    // never throw from analytics
  }
}
